/*
 * Una alerta vigente puede ser de VARIAS líneas (cards DP7i8uTf + hlfD0Dgd, Tanda 2): cuando dos o más
 * líneas miran las mismas campañas y miden el mismo número, el backend las junta en UN ítem que trae
 * `lines` (una entrada por línea, con su propio silencio) y `business_unit_names`.
 *
 * Todo lo que una bandeja necesita saber de esas líneas sale de acá: el nombre que se muestra, cuáles
 * están silenciadas y con qué coordenadas se silencia o se reactiva el grupo. Lo usan las filas
 * compartidas (`AlertItemRows`, que reusa «Ahora» del Centro de Alertas), el popover del Listado y el
 * silenciado (`agency/v2/alertSnooze.js`), así que las tres superficies no pueden contestar distinto.
 *
 * Con un payload viejo —sin `lines`— todo cae a la línea única de siempre (`business_unit_name`,
 * `snooze`): el FE puede salir antes que el backend.
 */

function linesOf(item) {
    return item && Array.isArray(item.lines) ? item.lines : [];
}

/** Los nombres de las líneas donde pasa la alerta, en el orden que manda el backend. */
export function alertLineNames(item) {
    if (!item) return [];
    const fromLines = linesOf(item).map((line) => line && line.business_unit_name).filter(Boolean);
    if (fromLines.length) return fromLines;
    if (Array.isArray(item.business_unit_names) && item.business_unit_names.length) {
        return item.business_unit_names.filter(Boolean);
    }
    return item.business_unit_name ? [item.business_unit_name] : [];
}

/**
 * Los ids de las líneas de una alerta (un ítem de la lista o el `latest` del KPI del Panel): todas las del
 * grupo; con un backend viejo, la línea única.
 */
export function alertLineIds(alert) {
    if (!alert) return [];
    if (Array.isArray(alert.business_unit_ids) && alert.business_unit_ids.length) return alert.business_unit_ids;
    return alert.business_unit_id != null ? [alert.business_unit_id] : [];
}

/** «A, B y C» en el idioma de la pantalla. Sin `Intl.ListFormat` (navegadores viejos), con comas. */
export function joinLineNames(names, language) {
    if (!names.length) return '';
    try {
        return new Intl.ListFormat(language || 'es', {style: 'long', type: 'conjunction'}).format(names);
    } catch (error) {
        return names.join(', ');
    }
}

/**
 * Las líneas silenciadas de un grupo que NO está silenciado entero (`[]` si no aplica): se nombran en
 * la fila, porque silenciar el grupo actúa sobre todas y el usuario tiene que ver que una ya estaba.
 */
export function partiallySnoozedLineNames(item) {
    const lines = linesOf(item);
    if (lines.length < 2 || (item && item.snoozed)) return [];
    return lines.filter((line) => line && line.snoozed).map((line) => line.business_unit_name).filter(Boolean);
}

const NO_ESCAPE = {interpolation: {escapeValue: false}};

/** «Línea X» o «Líneas A, B y C» (`null` si la alerta no es de ninguna línea: una URL, una cuenta). */
export function alertLinesText(item, t, language) {
    const names = alertLineNames(item);
    if (!names.length) return null;
    if (names.length === 1) return t('clients_v2.alert_meta_line', {name: names[0], ...NO_ESCAPE});
    return t('clients_v2.alert_meta_lines', {names: joinLineNames(names, language), ...NO_ESCAPE});
}

/** La meta de una fila silenciada: dónde sigue pasando. */
export function alertLinesStillText(item, t, language) {
    const names = alertLineNames(item);
    if (!names.length) return t('clients_v2.alert_meta_still');
    if (names.length === 1) return t('clients_v2.alert_meta_line_still', {name: names[0], ...NO_ESCAPE});
    return t('clients_v2.alert_meta_lines_still', {names: joinLineNames(names, language), ...NO_ESCAPE});
}

/** «silenciada en Videos» cuando el grupo está silenciado en parte (`null` si no). */
export function alertPartialSnoozeText(item, t, language) {
    const names = partiallySnoozedLineNames(item);
    if (!names.length) return null;
    return t('clients_v2.alert_meta_snoozed_in', {names: joinLineNames(names, language), ...NO_ESCAPE});
}

function availableCoordinates(lines) {
    return lines.filter((line) => line && line.snooze && line.snooze.available).map((line) => line.snooze);
}

/**
 * Con qué coordenadas se silencia el ítem: una por cada línea que todavía NO está silenciada. Sin
 * `lines`, las `snooze.targets` del grupo o, en un payload viejo, la coordenada única de siempre.
 */
export function snoozeTargetsOf(item) {
    if (!item) return [];
    const pending = availableCoordinates(linesOf(item).filter((line) => line && !line.snoozed));
    if (pending.length) return pending;
    const targets = item.snooze && Array.isArray(item.snooze.targets) ? item.snooze.targets : [];
    if (targets.length) return targets;
    return item.snooze ? [item.snooze] : [];
}

/**
 * Las coordenadas de las líneas YA silenciadas de un grupo vigente (`[]` sin `lines`). Al silenciar el
 * grupo se vuelven a silenciar con el plazo elegido (DELETE + POST): si no, una línea silenciada «Por hoy»
 * conservaría su plazo y mañana el problema volvería, aunque el modal dijo «se silencia en A, B y C».
 */
export function resnoozeTargetsOf(item) {
    return availableCoordinates(linesOf(item).filter((line) => line && line.snoozed));
}

/** Los nombres de esas líneas, para que el modal diga que pasan al plazo nuevo. */
export function resnoozedLineNames(item) {
    return linesOf(item).filter((line) => line && line.snoozed && line.snooze && line.snooze.available)
        .map((line) => line.business_unit_name).filter(Boolean);
}

/** Con qué coordenadas se reactiva: las de cada línea silenciada (o la coordenada única de siempre). */
export function unsnoozeTargetsOf(item) {
    if (!item) return [];
    const snoozed = availableCoordinates(linesOf(item).filter((line) => line && line.snoozed));
    if (snoozed.length) return snoozed;
    return item.snooze ? [item.snooze] : [];
}

/**
 * Las coordenadas sin repetir. Dos alertas pueden traer las mismas (el mismo flex en dos campañas de una
 * línea, una línea que comparten dos grupos): dos POST iguales en paralelo crean dos silencios, y el
 * backend no los frena (su `unique_together` lleva columnas en NULL). Después silenciar o reactivar esa
 * línea revienta con un 500.
 */
export function coordinatesKey(coords) {
    return [coords.scope, coords.business_unit_id, coords.indicator, coords.sub_indicator].join('|');
}

export function uniqueCoordinates(coordinates) {
    const seen = new Set();
    return coordinates.filter((coords) => {
        const key = coordinatesKey(coords);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function landedWith(response, alreadyCode) {
    if (!response) return false;
    return response.status === 'OK' || response.status === alreadyCode || response.error_code === alreadyCode;
}

/**
 * ¿Una respuesta de silenciar cuenta como «quedó silenciada»? Silenciar algo que ya lo estaba no es
 * una falla del usuario: el backend contesta `ERROR_ALREADY_SNOOZED` y la línea igual queda en silencio.
 */
export function snoozeLanded(response) {
    return landedWith(response, 'ERROR_ALREADY_SNOOZED');
}

/** Lo mismo al reactivar: `ERROR_NOT_SNOOZED` (alguien ya la reactivó) deja la línea como se pidió. */
export function unsnoozeLanded(response) {
    return landedWith(response, 'ERROR_NOT_SNOOZED');
}
